/* eslint-disable react-hooks/exhaustive-deps */
import currency from "currency.js";
import { useMemo } from "react";
import { useSortBy, useTable } from "react-table";
import Collapse from "antd/es/collapse/Collapse";
import { COLORS, TEXT_COLORS } from "../../../constants/design/colors";
import { IAevoPosition } from "../../../interfaces/aevo";
import { DefaultCellForColumn } from "../../Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../../Table/DefaultHeaderForColumn";
import { ITableColumn } from "../../Table/TableColumn";
import { Align, MinifiedTableWrapper, TableHeaderCell, TableLoader } from "../../Table/style";
import { Spinner } from "../../shared/Spinner";
import { IDeribitPosition, IDeribitAccountSummary } from "../../../interfaces/deribit";
import { retrieveAssetByInstrumentName } from "../../../utils/strings";
import CollapseHeaderCount from "../../TableCollapse";
import { useMarkets } from "../../../hooks/api/aevo/useMarkets";

interface IOptionsAggregatedPosition {
  instrument_name: string;
  asset: string;

  // Contracts
  aevoContracts: number;
  deribitContracts: number;
  netContracts: number;

  // Prices
  averagePrice: number;
  aevoMarkPrice: number;
  deribitMarkPrice: number; // In USD
  deribitMarkPriceRaw: number; // In BTC/ETH
  spotPrice: number; // Spot price for converting Deribit prices

  // Aevo Greeks
  aevoDelta: number;
  aevoGamma: number;
  aevoVega: number;
  aevoTheta: number;

  // Deribit Greeks
  deribitDelta: number;
  deribitGamma: number;
  deribitVega: number;
  deribitTheta: number;

  // Net Greeks (sum of both exchanges)
  netDelta: number;
  netGamma: number;
  netVega: number;
  netTheta: number;

  // PnL
  pnl: number;

  // Derived
  side: "Long" | "Short" | "-";
}

// Normalize instrument names to Aevo convention
// 1. ETH-PERP -> ETH-PERPETUAL
// 2. ETH-2NOV25-4050-P -> ETH-02NOV25-4050-P (add leading zero to day)
const normalizeInstrumentName = (name: string): string => {
  // Handle PERP -> PERPETUAL
  if (name.endsWith("-PERP")) {
    return name.replace("-PERP", "-PERPETUAL");
  }

  // Handle Deribit date format (e.g., ETH-2NOV25 -> ETH-02NOV25)
  // Pattern: ASSET-DDMMMYY-STRIKE-TYPE
  const datePattern = /^([A-Z]+)-(\d{1,2})([A-Z]{3}\d{2})-(.+)$/;
  const match = name.match(datePattern);

  if (match) {
    const [, asset, day, rest, strikeAndType] = match;
    const paddedDay = day.padStart(2, "0");
    return `${asset}-${paddedDay}${rest}-${strikeAndType}`;
  }

  return name;
};

// Helper function to get color for delta values
const getDeltaColor = (value: number) => {
  if (value > 0) return COLORS.green.one;
  if (value < 0) return COLORS.red.one;
  return TEXT_COLORS.one;
};

// Sort function for options positions (by net delta absolute value)
const sortOptionsPositions = (positions: IOptionsAggregatedPosition[]) =>
  positions.sort((a, b) => Math.abs(b.netDelta) - Math.abs(a.netDelta));

interface IOptionsAggregatedPositionTableProps {
  aevoPositions: IAevoPosition[];
  deribitPositions: IDeribitPosition[];
  selectedAsset?: string;
  isLoading?: boolean;
  instrumentFilter?: string;
  deribitAccountData?: {
    BTC?: IDeribitAccountSummary;
    ETH?: IDeribitAccountSummary;
    SOL?: IDeribitAccountSummary;
  };
}

export function OptionsAggregatedPositionTable({
  aevoPositions = [],
  deribitPositions = [],
  selectedAsset,
  isLoading,
  instrumentFilter = "",
  deribitAccountData,
}: IOptionsAggregatedPositionTableProps) {
  const { data: marketsData } = useMarkets();

  // Get spot prices for BTC, ETH, SOL
  const spotPrices = useMemo(() => {
    const prices: { [key: string]: number } = {};
    if (marketsData) {
      marketsData.forEach((market) => {
        if (market.instrument_type === "PERPETUAL") {
          prices[market.underlying_asset] = Number(market.mark_price);
        }
      });
    }
    return prices;
  }, [marketsData]);

  const memoizedPositions = useMemo(() => {
    // Create a map of instrument_name to aggregated position
    const positionMap: { [key: string]: IOptionsAggregatedPosition } = {};

    // First, create a map of Deribit per-contract Greeks by instrument
    const deribitGreeksMap: { [key: string]: { delta: number; gamma: number; vega: number; theta: number } } = {};
    deribitPositions.forEach((pos) => {
      const normalizedName = normalizeInstrumentName(pos.instrument_name || "");
      const size = Number(pos.size || 0);

      // Deribit Greeks are for the TOTAL position
      // Divide by SIGNED size to get per-contract Greeks with correct sign for option type
      // (calls will be positive delta, puts will be negative delta)
      if (size !== 0) {
        deribitGreeksMap[normalizedName] = {
          delta: Number(pos.delta || 0) / size,
          gamma: Math.abs(Number(pos.gamma || 0)) / Math.abs(size), // Gamma per-contract is always positive
          vega: Math.abs(Number(pos.vega || 0)) / Math.abs(size), // Vega per-contract is always positive
          theta: Number(pos.theta || 0) / size, // Theta can be positive or negative
        };
      }
    });

    // Process Aevo positions
    aevoPositions.forEach((pos) => {
      const normalizedName = normalizeInstrumentName(pos.instrument_name);
      const asset = retrieveAssetByInstrumentName(normalizedName);
      const amount = Number(pos.amount);
      let side = 0;
      if (pos.side === "buy") {
        side = 1;
      } else if (pos.side === "sell") {
        side = -1;
      }
      const contracts = amount * side;
      const spotPrice = spotPrices[asset] || 0;

      if (!positionMap[normalizedName]) {
        positionMap[normalizedName] = {
          instrument_name: normalizedName,
          asset,
          aevoContracts: 0,
          deribitContracts: 0,
          netContracts: 0,
          averagePrice: 0,
          aevoMarkPrice: 0,
          deribitMarkPrice: 0,
          deribitMarkPriceRaw: 0,
          spotPrice,
          aevoDelta: 0,
          aevoGamma: 0,
          aevoVega: 0,
          aevoTheta: 0,
          deribitDelta: 0,
          deribitGamma: 0,
          deribitVega: 0,
          deribitTheta: 0,
          netDelta: 0,
          netGamma: 0,
          netVega: 0,
          netTheta: 0,
          pnl: 0,
          side: "-",
        };
      }

      positionMap[normalizedName].aevoContracts += contracts;
      positionMap[normalizedName].aevoMarkPrice = Number(pos.mark_price);
      positionMap[normalizedName].pnl += Number(pos.unrealized_pnl);

      // Calculate Aevo Greeks using Deribit per-contract Greeks if available
      if (pos.option) {
        const deribitGreeks = deribitGreeksMap[normalizedName];

        if (deribitGreeks) {
          // Use Deribit per-contract Greeks * Aevo contracts
          positionMap[normalizedName].aevoDelta += deribitGreeks.delta * contracts;
          positionMap[normalizedName].aevoGamma += deribitGreeks.gamma * contracts;
          positionMap[normalizedName].aevoVega += deribitGreeks.vega * contracts;
          positionMap[normalizedName].aevoTheta += deribitGreeks.theta * contracts;
        } else {
          // Fallback: Aevo Greeks are already total, just apply sign
          positionMap[normalizedName].aevoDelta += Number(pos.option.delta || 0) * side;
          positionMap[normalizedName].aevoGamma += Number(pos.option.gamma || 0) * side;
          positionMap[normalizedName].aevoVega += Number(pos.option.vega || 0) * side;
          positionMap[normalizedName].aevoTheta += Number(pos.option.theta || 0) * side;
        }
      }
    });

    // Process Deribit positions
    deribitPositions.forEach((pos) => {
      const normalizedName = normalizeInstrumentName(pos.instrument_name || "");
      const asset = retrieveAssetByInstrumentName(normalizedName);
      // Deribit size is already signed (positive for long, negative for short)
      const contracts = Number(pos.size || 0);
      const spotPrice = spotPrices[asset] || 0;
      const deribitMarkPriceRaw = Number(pos.mark_price || 0);
      // For PERPETUAL instruments, mark price is already in USD
      // For options, mark price is in BTC/ETH and needs to be converted
      const isPerpetual = normalizedName.includes("PERPETUAL");
      const deribitMarkPriceUsd = isPerpetual ? deribitMarkPriceRaw : deribitMarkPriceRaw * spotPrice;

      if (!positionMap[normalizedName]) {
        positionMap[normalizedName] = {
          instrument_name: normalizedName,
          asset,
          aevoContracts: 0,
          deribitContracts: 0,
          netContracts: 0,
          averagePrice: 0,
          aevoMarkPrice: 0,
          deribitMarkPrice: 0,
          deribitMarkPriceRaw: 0,
          spotPrice,
          aevoDelta: 0,
          aevoGamma: 0,
          aevoVega: 0,
          aevoTheta: 0,
          deribitDelta: 0,
          deribitGamma: 0,
          deribitVega: 0,
          deribitTheta: 0,
          netDelta: 0,
          netGamma: 0,
          netVega: 0,
          netTheta: 0,
          pnl: 0,
          side: "-",
        };
      }

      positionMap[normalizedName].deribitContracts += contracts;
      positionMap[normalizedName].deribitMarkPriceRaw = deribitMarkPriceRaw;
      positionMap[normalizedName].deribitMarkPrice = deribitMarkPriceUsd;

      // Calculate PnL for options
      const pnl = pos.kind === "future"
        ? (Number(pos.average_price || 0) - Number(pos.mark_price || 0)) * Math.abs(contracts)
        : Number(pos.floating_profit_loss_usd || 0);
      positionMap[normalizedName].pnl += pnl;

      // Add Greeks from Deribit
      // Deribit Greeks are already the TOTAL for the position with correct signs
      // (short puts will have positive delta, short options will have positive theta, etc.)
      positionMap[normalizedName].deribitDelta += Number(pos.delta || 0);
      positionMap[normalizedName].deribitGamma += Number(pos.gamma || 0);
      positionMap[normalizedName].deribitVega += Number(pos.vega || 0);
      positionMap[normalizedName].deribitTheta += Number(pos.theta || 0);
    });

    // Process Deribit balance
    if (deribitAccountData) {
      Object.keys(deribitAccountData).forEach((asset) => {
        const accountData = deribitAccountData[asset as keyof typeof deribitAccountData];
        if (accountData && accountData.balance !== undefined) {
          const balance = Number(accountData.balance || 0);
          if (balance !== 0) {
            const instrumentName = `${asset}-BALANCE`;
            const spotPrice = spotPrices[asset] || 0;

            if (!positionMap[instrumentName]) {
              positionMap[instrumentName] = {
                instrument_name: instrumentName,
                asset,
                aevoContracts: 0,
                deribitContracts: 0,
                netContracts: 0,
                averagePrice: 0,
                aevoMarkPrice: 0,
                deribitMarkPrice: 0,
                deribitMarkPriceRaw: 0,
                spotPrice,
                aevoDelta: 0,
                aevoGamma: 0,
                aevoVega: 0,
                aevoTheta: 0,
                deribitDelta: balance, // Balance delta equals the balance amount
                deribitGamma: 0,
                deribitVega: 0,
                deribitTheta: 0,
                netDelta: 0,
                netGamma: 0,
                netVega: 0,
                netTheta: 0,
                pnl: 0,
                side: "-",
              };
            } else {
              // If somehow the entry already exists, update the delta
              positionMap[instrumentName].deribitDelta = balance;
            }
          }
        }
      });
    }

    // Calculate net values and determine side
    /* eslint-disable no-param-reassign */
    Object.values(positionMap).forEach((pos) => {
      pos.netContracts = pos.aevoContracts + pos.deribitContracts;

      if (pos.netContracts > 0) {
        pos.side = "Long";
      } else if (pos.netContracts < 0) {
        pos.side = "Short";
      } else {
        pos.side = "-";
      }

      // Calculate net Greeks
      pos.netDelta = pos.aevoDelta + pos.deribitDelta;
      pos.netGamma = pos.aevoGamma + pos.deribitGamma;
      pos.netVega = pos.aevoVega + pos.deribitVega;
      pos.netTheta = pos.aevoTheta + pos.deribitTheta;

      // Calculate weighted average price
      const aevoValue = pos.aevoContracts * pos.aevoMarkPrice;
      const deribitValue = pos.deribitContracts * pos.deribitMarkPrice;
      const totalContracts = Math.abs(pos.aevoContracts) + Math.abs(pos.deribitContracts);
      pos.averagePrice = totalContracts > 0
        ? (Math.abs(aevoValue) + Math.abs(deribitValue)) / totalContracts
        : 0;
    });
    /* eslint-enable no-param-reassign */

    // Convert to array, filter by selected asset and instrument filter, and sort
    let positions = Object.values(positionMap);

    if (selectedAsset) {
      positions = positions.filter((pos) => pos.asset === selectedAsset);
    }

    if (instrumentFilter) {
      const filterLower = instrumentFilter.toLowerCase();
      positions = positions.filter((pos) =>
        pos.instrument_name.toLowerCase().includes(filterLower)
      );
    }

    return sortOptionsPositions(positions);
  }, [aevoPositions, deribitPositions, selectedAsset, spotPrices, instrumentFilter, deribitAccountData]);

  const columns: ITableColumn<IOptionsAggregatedPosition>[] = useMemo(
    () => [
      {
        title: "Instrument Name",
        align: "left",
        accessor: "instrument_name",
        Cell: ({ value }) => <div style={{ fontFamily: "monospace" }}>{value}</div>,
      },
      {
        title: "Net Side",
        align: "left",
        accessor: "netContracts",
        id: "net_side",
        Cell: ({ value }) => (
          <div
            style={{
              color:
                // eslint-disable-next-line no-nested-ternary
                value === 0 ? TEXT_COLORS.three : value > 0 ? COLORS.green.one : COLORS.red.one,
            }}
          >
            {/* eslint-disable-next-line no-nested-ternary */}
            {value === 0 ? <span style={{ color: TEXT_COLORS.three }}>-</span> : value > 0 ? "Long" : "Short"}
          </div>
        ),
      },
      {
        title: "Net Contracts",
        align: "right",
        accessor: "netContracts",
        Cell: ({ value }) => {
          let color: string = TEXT_COLORS.three;
          if (value > 0) {
            color = COLORS.green.one;
          } else if (value < 0) {
            color = COLORS.red.one;
          }
          return (
            <Align
              align="right"
              style={{
                fontWeight: 600,
                color,
              }}
            >
              {currency(value).format({ symbol: "" })}
            </Align>
          );
        },
        sortType: (a: any, b: any) => {
          const aValue = a.original.netContracts;
          const bValue = b.original.netContracts;
          return aValue - bValue;
        },
      },
      {
        title: "Aevo Contracts",
        align: "right",
        accessor: "aevoContracts",
        Cell: ({ value }) => (
          <Align align="right">
            {value !== 0 ? (
              <div style={{ color: getDeltaColor(value) }}>
                {currency(value).format({ symbol: "" })}
              </div>
            ) : (
              <span style={{ color: TEXT_COLORS.three }}>-</span>
            )}
          </Align>
        ),
        sortType: (a: any, b: any) => {
          const aValue = a.original.aevoContracts;
          const bValue = b.original.aevoContracts;
          return aValue - bValue;
        },
      },
      {
        title: "Deribit Contracts",
        align: "right",
        accessor: "deribitContracts",
        Cell: ({ value }) => (
          <Align align="right">
            {value !== 0 ? (
              <div style={{ color: getDeltaColor(value) }}>
                {currency(value).format({ symbol: "" })}
              </div>
            ) : (
              <span style={{ color: TEXT_COLORS.three }}>-</span>
            )}
          </Align>
        ),
        sortType: (a: any, b: any) => {
          const aValue = a.original.deribitContracts;
          const bValue = b.original.deribitContracts;
          return aValue - bValue;
        },
      },
      {
        title: "Aevo Mark Price",
        align: "right",
        accessor: "aevoMarkPrice",
        Cell: ({ value }) => (
          <Align align="right">
            {value > 0 ? (
              `$${Number(value).toLocaleString("en-US", { maximumSignificantDigits: 6, minimumSignificantDigits: 1 })}`
            ) : (
              <span style={{ color: TEXT_COLORS.three }}>-</span>
            )}
          </Align>
        ),
      },
      {
        title: "Deribit Mark Price",
        align: "right",
        accessor: "deribitMarkPrice",
        Cell: ({ value }) => (
          <Align align="right">
            {value > 0 ? (
              `$${Number(value).toLocaleString("en-US", { maximumSignificantDigits: 6, minimumSignificantDigits: 1 })}`
            ) : (
              <span style={{ color: TEXT_COLORS.three }}>-</span>
            )}
          </Align>
        ),
      },
      {
        title: "Aevo Delta",
        align: "right",
        accessor: "aevoDelta",
        Cell: ({ value }) => (
          <Align align="right" style={{ color: getDeltaColor(value) }}>
            {value !== 0 ? currency(value, { precision: 4 }).format({ symbol: "" }) : "-"}
          </Align>
        ),
        sortType: (a: any, b: any) => {
          const aValue = a.original.aevoDelta;
          const bValue = b.original.aevoDelta;
          return aValue - bValue;
        },
      },
      {
        title: "Deribit Delta",
        align: "right",
        accessor: "deribitDelta",
        Cell: ({ value }) => (
          <Align align="right" style={{ color: getDeltaColor(value) }}>
            {value !== 0 ? currency(value, { precision: 4 }).format({ symbol: "" }) : "-"}
          </Align>
        ),
        sortType: (a: any, b: any) => {
          const aValue = a.original.deribitDelta;
          const bValue = b.original.deribitDelta;
          return aValue - bValue;
        },
      },
      {
        title: "Net Delta",
        align: "right",
        accessor: "netDelta",
        Cell: ({ value }) => (
          <Align
            align="right"
            style={{
              color: getDeltaColor(value),
              fontWeight: 600,
            }}
          >
            {value !== 0 ? currency(value, { precision: 4 }).format({ symbol: "" }) : "-"}
          </Align>
        ),
        sortType: (a: any, b: any) => {
          const aValue = a.original.netDelta;
          const bValue = b.original.netDelta;
          return aValue - bValue;
        },
      },
      {
        title: "Aevo Gamma",
        align: "right",
        accessor: "aevoGamma",
        Cell: ({ value }) => (
          <Align align="right">
            {value !== 0 ? (
              currency(value, { precision: 4 }).format({ symbol: "" })
            ) : (
              <span style={{ color: TEXT_COLORS.three }}>-</span>
            )}
          </Align>
        ),
        sortType: (a: any, b: any) => {
          const aValue = a.original.aevoGamma;
          const bValue = b.original.aevoGamma;
          return aValue - bValue;
        },
      },
      {
        title: "Deribit Gamma",
        align: "right",
        accessor: "deribitGamma",
        Cell: ({ value }) => (
          <Align align="right">
            {value !== 0 ? (
              currency(value, { precision: 4 }).format({ symbol: "" })
            ) : (
              <span style={{ color: TEXT_COLORS.three }}>-</span>
            )}
          </Align>
        ),
        sortType: (a: any, b: any) => {
          const aValue = a.original.deribitGamma;
          const bValue = b.original.deribitGamma;
          return aValue - bValue;
        },
      },
      {
        title: "Net Gamma",
        align: "right",
        accessor: "netGamma",
        Cell: ({ value }) => (
          <Align align="right">
            {value !== 0 ? (
              currency(value, { precision: 4 }).format({ symbol: "" })
            ) : (
              <span style={{ color: TEXT_COLORS.three }}>-</span>
            )}
          </Align>
        ),
        sortType: (a: any, b: any) => {
          const aValue = a.original.netGamma;
          const bValue = b.original.netGamma;
          return aValue - bValue;
        },
      },
      {
        title: "Aevo Vega",
        align: "right",
        accessor: "aevoVega",
        Cell: ({ value }) => (
          <Align align="right">
            {value !== 0 ? (
              currency(value, { precision: 4 }).format({ symbol: "" })
            ) : (
              <span style={{ color: TEXT_COLORS.three }}>-</span>
            )}
          </Align>
        ),
        sortType: (a: any, b: any) => {
          const aValue = a.original.aevoVega;
          const bValue = b.original.aevoVega;
          return aValue - bValue;
        },
      },
      {
        title: "Deribit Vega",
        align: "right",
        accessor: "deribitVega",
        Cell: ({ value }) => (
          <Align align="right">
            {value !== 0 ? (
              currency(value, { precision: 4 }).format({ symbol: "" })
            ) : (
              <span style={{ color: TEXT_COLORS.three }}>-</span>
            )}
          </Align>
        ),
        sortType: (a: any, b: any) => {
          const aValue = a.original.deribitVega;
          const bValue = b.original.deribitVega;
          return aValue - bValue;
        },
      },
      {
        title: "Net Vega",
        align: "right",
        accessor: "netVega",
        Cell: ({ value }) => (
          <Align align="right">
            {value !== 0 ? (
              currency(value, { precision: 4 }).format({ symbol: "" })
            ) : (
              <span style={{ color: TEXT_COLORS.three }}>-</span>
            )}
          </Align>
        ),
        sortType: (a: any, b: any) => {
          const aValue = a.original.netVega;
          const bValue = b.original.netVega;
          return aValue - bValue;
        },
      },
      {
        title: "Aevo Theta",
        align: "right",
        accessor: "aevoTheta",
        Cell: ({ value }) => (
          <Align align="right">
            {value !== 0 ? (
              currency(value, { precision: 4 }).format({ symbol: "" })
            ) : (
              <span style={{ color: TEXT_COLORS.three }}>-</span>
            )}
          </Align>
        ),
        sortType: (a: any, b: any) => {
          const aValue = a.original.aevoTheta;
          const bValue = b.original.aevoTheta;
          return aValue - bValue;
        },
      },
      {
        title: "Deribit Theta",
        align: "right",
        accessor: "deribitTheta",
        Cell: ({ value }) => (
          <Align align="right">
            {value !== 0 ? (
              currency(value, { precision: 4 }).format({ symbol: "" })
            ) : (
              <span style={{ color: TEXT_COLORS.three }}>-</span>
            )}
          </Align>
        ),
        sortType: (a: any, b: any) => {
          const aValue = a.original.deribitTheta;
          const bValue = b.original.deribitTheta;
          return aValue - bValue;
        },
      },
      {
        title: "Net Theta",
        align: "right",
        accessor: "netTheta",
        Cell: ({ value }) => (
          <Align align="right">
            {value !== 0 ? (
              currency(value, { precision: 4 }).format({ symbol: "" })
            ) : (
              <span style={{ color: TEXT_COLORS.three }}>-</span>
            )}
          </Align>
        ),
        sortType: (a: any, b: any) => {
          const aValue = a.original.netTheta;
          const bValue = b.original.netTheta;
          return aValue - bValue;
        },
      },
      {
        title: "Unrealized PnL",
        align: "right",
        accessor: "pnl",
        Cell: ({ value }) => {
          let color: string = TEXT_COLORS.one;
          if (value > 0) {
            color = COLORS.green.one;
          } else if (value < 0) {
            color = COLORS.red.one;
          }
          return (
            <Align
              align="right"
              style={{
                color,
              }}
            >
              {currency(Number(value)).format()}
            </Align>
          );
        },
        sortType: (a: any, b: any) => {
          const aValue = a.original.pnl;
          const bValue = b.original.pnl;
          return aValue - bValue;
        },
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns: columns as any,
      data: memoizedPositions,
      defaultColumn: {
        Header: DefaultHeaderForColumn,
        Cell: DefaultCellForColumn,
      } as any,
      autoResetSortBy: false,
    },
    useSortBy
  );

  return (
    <Collapse
      bordered={false}
      defaultActiveKey={["1"]}
      expandIconPosition="end"
      style={{ backgroundColor: "transparent" }}
    >
      <Collapse.Panel
        header={<CollapseHeaderCount title="Options Positions" count={memoizedPositions.length} />}
        key="1"
        style={{ backgroundColor: "transparent", border: "none" }}
      >
        {(() => {
          if (isLoading && memoizedPositions.length === 0) {
            return <TableLoader />;
          }

          if (memoizedPositions.length === 0) {
            return (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: TEXT_COLORS.three,
                  backgroundColor: "#1a1a1a",
                  borderRadius: "8px",
                }}
              >
                No options positions found
              </div>
            );
          }

          return (
            <div style={{ position: "relative" }}>
              {isLoading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.3)",
                  zIndex: 100,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Spinner />
              </div>
            )}
              <MinifiedTableWrapper {...getTableProps()}>
                <thead
                  style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  backgroundColor: "#0a0a0a",
                }}
                >
                  {headerGroups.map((headerGroup, headerIndex) => (
                    <tr {...headerGroup.getHeaderGroupProps()} key={headerIndex}>
                      {headerGroup.headers.map((column, columnIndex) => (
                        <TableHeaderCell
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                          key={columnIndex}
                        >
                          {column.render("Header")}
                        </TableHeaderCell>
                    ))}
                    </tr>
                ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {rows.map((row, rowIndex) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()} key={rowIndex}>
                      {row.cells.map((cell, cellIndex) => (
                        <td {...cell.getCellProps()} key={cellIndex}>
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                </tbody>
              </MinifiedTableWrapper>
            </div>
          );
        })()}
      </Collapse.Panel>
    </Collapse>
  );
}
